import React, { PureComponent, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import { createToast, updateToast, dismissToast } from 'services/alert-service';

import { removeBackgroundTask } from 'actions/background-tasks';
import Alert from './alert';

import IconCheckCircled from '../icons/icon-check-circled';

const BackgroundTask = ({ id, progress, type, content }) => {
    useEffect(() => {
        createToast(type, content, { progress, autoClose: false, toastId: id });
    }, []);

    useEffect(() => {
        if (progress === 1) {
            updateToast(id, { progress: null, type, render: () => content, autoClose: 5000 });
        } else {
            updateToast(id, { progress, type, render: () => content });
        }
    }, [progress, type, content]);

    return null;
}

class BackgroundTaskProgress extends PureComponent {
    timeouts = [];
    errorTasks = [];

    state = {
        tasks: this.props.tasks
    }

    getTaskProgress(task) {
        if (task.completed) {
            return 1;
        }

        if (!task.current || !task.total) {
            return 0;
        }

        return Math.round((task.current / task.total) * 100) / 100;
    }

    getMessage(task) {
        const { t } = this.props;
        const action = t(`backgroundTasks.types.${task.type}`);

        if (task.error) {
            return t('backgroundTasks.error', { action })
        } else if (task.completed) {
            return t('backgroundTasks.completed', { action })
        }

        const progress = this.getTaskProgress(task);

        if (progress) {
            return t('backgroundTasks.progress', { action, progress: progress * 100 })
        }

        return t('backgroundTasks.progressWithoutPercentage', { action })
    }

    addTasks(tasks) {
        if (!tasks.length) {
            return;
        }
        
        this.setState({
            tasks: [...this.state.tasks, ...tasks]
        });
    }

    updateTasks(tasks) {
        if (!tasks.length) {
            return;
        }

        if (tasks.some(task => !this.state.tasks.some(t => t.id === task.id))) {
            return;
        }

        this.setState({
            tasks: [...this.state.tasks].map(task => {
                const updatedTask = tasks.find(t => t.id === task.id);
                if (updatedTask) {
                    return updatedTask;
                }

                return task;
            })
        });
    }

    completeTasks(tasks) {
        if (!tasks.length) {
            return;
        }

        if (tasks.some(task => !this.state.tasks.some(t => t.id === task.id))) {
            setTimeout(() => this.completeTasks(tasks));
            return;
        }

        this.setState({
            tasks: [...this.state.tasks].map(task => {
                const completedTask = tasks.find(t => t.id === task.id);
                if (completedTask) {
                    return {
                        ...task,
                        completed: true
                    };
                }

                return task;
            })
        });

        this.timeouts.push(setTimeout(() => {
            this.removeTasks(tasks);
        }, 3000));
    }

    removeTasks(tasks) {
        if (!tasks.length) {
            return;
        }

        this.setState({
            tasks: this.state.tasks.filter(task => !tasks.some(t => t.id === task.id))
        });
    }

    clearErrorTasks(tasks) {
        if (!tasks.length) {
            return;
        }

        this.errorTasks = [...this.errorTasks, ...tasks.map(task => task.id)];

        this.timeouts.push(setTimeout(() => {
            this.removeTasks(tasks);
        }, 3000));

        tasks.forEach(task => removeBackgroundTask(task.id));
    }

    componentDidUpdate(prevProps) {
        if (prevProps.tasks !== this.props.tasks) {
            // New tasks
            const newTasks = this.props.tasks.filter(task => !prevProps.tasks.some(t => t.id === task.id));

            // Completed tasks
            const completedTasks = prevProps.tasks.filter(task => !this.props.tasks.some(t => t.id === task.id) && !this.errorTasks.includes(task.id));

            // Updated tasks
            const updatedTasks = this.props.tasks.filter(task => task !== prevProps.tasks.find(t => t.id !== task.id));

            // Error tasks
            const errorTasks = this.props.tasks.filter(task => task.error);

            this.addTasks(newTasks);
            this.updateTasks(updatedTasks);
            this.completeTasks(completedTasks);
            this.clearErrorTasks(errorTasks);
        }
    }

    componentWillUnmount() {
        this.timeouts.forEach(t => clearTimeout(t));
    }

    render() {
        return this.state.tasks.map((task, index) => (
            <BackgroundTask key={task.id || index} id={task.id || index} progress={this.getTaskProgress(task)} content={this.getMessage(task)} type={task.error ? 'error' : task.completed ? 'success' : 'loading'} />
        ));
        // return this.state.tasks.map(task => (
        //     <Alert
        //         key={task.id}
        //         collapsed={!task.completed && !task.error}
        //         progress={this.getTaskProgress(task)}
        //         type={task.error ? 'error' : 'success'}
        //         icon={task.error ? undefined : (task.completed ? <IconCheckCircled /> : <i className="icon material-icons icon-animate-rotate">autorenew</i>)}
        //      >
        //         { this.getMessage(task) }
        //     </Alert>
        // ));
    }
}

const makeMapStateToProps = () => {
    return ({ backgroundTasks }) => ({
        tasks: backgroundTasks.tasks
    });
};

export default connect(makeMapStateToProps)(withTranslation('common')(BackgroundTaskProgress));
